import type { CountryCode, E164Number, NationalNumber } from 'libphonenumber-js/types'
import {
  formatIncompletePhoneNumber,
  parseIncompletePhoneNumber,
  parsePhoneNumber,
  AsYouType,
  isPossiblePhoneNumber,
} from 'libphonenumber-js/max'

export function isValidPhoneNumber(phoneNumber: string, countryCode?: string): boolean {
  // Check if phoneNumber is possible first or parsePhoneNumber will throw errors
  if (!isPossiblePhoneNumber(phoneNumber, countryCode as CountryCode)) {
    return false
  }

  const phone = parsePhoneNumber(phoneNumber, countryCode as CountryCode)

  if (!phone) {
    return false
  }

  // Check if defaultCountry we provide is actually matching the country entered
  // We need this check, because if a correct international number is provided (for any country), the library will simply say that
  // the number isValid even if you provide Australian international number, but want it to be formatted as US number
  if (phone.country !== countryCode) {
    return false
  }

  return phone.isValid()
}

export function toNationalPretty(phoneNumber?: string, countryCode?: string): NationalNumber | string {
  if (!phoneNumber) {
    return ''
  }

  const country = countryCode ?? getPhoneNumberCountry(phoneNumber)
  return isValidPhoneNumber(phoneNumber, country)
    ? parsePhoneNumber(phoneNumber, country as CountryCode).formatNational()
    : asIsPretty(phoneNumber, country as CountryCode)
}

export function toInternationalPretty(phoneNumber: string): E164Number | string {
  return parsePhoneNumber(phoneNumber).formatInternational()
}

export function toInternational(phoneNumber: string, countryCode?: string): E164Number | string {
  return isValidPhoneNumber(phoneNumber, countryCode)
    ? parsePhoneNumber(phoneNumber, countryCode as CountryCode).number
    : asIs(phoneNumber)
}

export function asIs(phoneNumber: string): string {
  return parseIncompletePhoneNumber(phoneNumber)
}

export function asIsPretty(phoneNumber: string, countryCode?: CountryCode): string {
  return formatIncompletePhoneNumber(phoneNumber, countryCode)
}

export function getPhoneNumberCountry(phoneNumber: string) {
  // Can only parse phone number from international format
  const asYouType = new AsYouType()
  asYouType.input(phoneNumber)
  return asYouType.getCountry()
}

export function formattedNumber(number: string) {
  const phoneNumber = parsePhoneNumber(number)
  return phoneNumber.formatNational()
}
